import React, { useCallback, useEffect, useState } from "react";
import { CalendarRange } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import CatalogListLayout from "../../components/task-board/CatalogListLayout";
import { SprintFormModal } from "../../components/task-board/CatalogFormModals";
import { StatusPill, daysUntil, formatDate } from "../../components/task-board/shared";

const timing = (s) => {
  if (s.status === "completed") return <span className="text-gray-400 dark:text-light-500">Completed</span>;
  const toStart = daysUntil(s.startDate);
  if (toStart > 0) return `Starts in ${toStart}d`;
  const left = daysUntil(s.endDate);
  if (left < 0) return <span className="text-red-500">Overdue by {-left}d</span>;
  return <span className="text-primary-accent font-medium">{left}d left</span>;
};

const Sprints = () => {
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSprints((await sprintService.list()).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load sprints"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <CatalogListLayout
        title="Sprints"
        subtitle="Workspace sprints. A sprint can hold tasks from any project and squad."
        icon={CalendarRange}
        canCreate={admin}
        createLabel="New sprint"
        onCreate={() => setCreating(true)}
        loading={loading}
        error={error}
        onRetry={load}
        items={sprints}
        emptyTitle="No sprints yet"
        emptyMessage={admin ? "Create a sprint with its goal, dates and milestones." : "An admin has not planned any sprints yet."}
        rowLink={(s) => `/task-board/sprints/${s._id}`}
        columns={[
          { header: "Sprint", render: (s) => <span className="whitespace-nowrap">{s.name}</span> },
          { header: "Status", render: (s) => <StatusPill status={s.status} /> },
          { header: "Start", className: "whitespace-nowrap", render: (s) => formatDate(s.startDate) },
          { header: "End", className: "whitespace-nowrap", render: (s) => formatDate(s.endDate) },
          { header: "Timing", className: "whitespace-nowrap", render: (s) => timing(s) },
          {
            header: "Goal",
            className: "max-w-sm",
            render: (s) => <span className="block truncate" title={s.goal}>{s.goal || "—"}</span>,
          },
        ]}
      />
      <SprintFormModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          load();
        }}
      />
    </>
  );
};

export default Sprints;
